<template>
  <div>
    <empty-card :title="$t('push-notifications.title', 'Push notifications')">
      <div class="push-notifications-form">
        <b-link
          :to="{ name: 'admin:accounts' }"
          class="d-inline-block mb-3"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="18"
            class="mr-50"
          />
          {{ $t('general.back', 'Back') }}
        </b-link>

        <b-form
          @submit.prevent="onSubmit"
        >
          <b-form-group
            :label="$t('push-notifications.filters', 'Filters (users by page type)')"
            label-class="font-weight-bold"
          >
            <b-form-checkbox
              v-model="form.has_active_pages"
              class="mb-50"
            >
              {{ $t('push-notifications.has-active-pages', 'Users with active pages') }}
            </b-form-checkbox>
            <b-form-checkbox
              v-model="form.has_expired_pages"
              class="mb-50"
            >
              {{ $t('push-notifications.has-expired-pages', 'Users with expired pages') }}
            </b-form-checkbox>
            <b-form-checkbox
              v-model="form.has_trial_pages"
              class="mb-50"
            >
              {{ $t('push-notifications.has-trial-pages', 'Users with trial pages') }}
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
            :label="$t('push-notifications.date-range', 'Date range (user registration)')"
            label-class="font-weight-bold"
          >
            <div class="d-flex flex-wrap align-items-center">
              <ok-date-time-picker
                v-model="form.date_from"
                class="mr-2 mb-1"
                hide-label
              />
              <span class="mx-2 mb-1">–</span>
              <ok-date-time-picker
                v-model="form.date_to"
                class="mb-1"
                hide-label
              />
            </div>
          </b-form-group>

          <b-form-group
            :label="$t('push-notifications.subject', 'Subject')"
            label-for="subject"
            label-class="font-weight-bold"
          >
            <b-form-input
              id="subject"
              v-model="form.subject"
              type="text"
              required
              maxlength="255"
              :placeholder="$t('push-notifications.subject-placeholder', 'Notification title')"
            />
          </b-form-group>

          <b-form-group
            :label="$t('push-notifications.body', 'Body')"
            label-for="body"
            label-class="font-weight-bold"
          >
            <b-form-textarea
              id="body"
              v-model="form.body"
              required
              rows="4"
              maxlength="2000"
              :placeholder="$t('push-notifications.body-placeholder', 'Notification message')"
            />
          </b-form-group>

          <b-form-group
            :label="$t('push-notifications.image', 'Image (optional)')"
            label-for="image"
            label-class="font-weight-bold"
          >
            <b-form-input
              id="image"
              v-model="form.image"
              type="url"
              class="mb-2"
              :placeholder="$t('push-notifications.image-placeholder', 'https://...')"
            />
            <div class="text-muted small mb-1">
              {{ $t('push-notifications.or-choose-from-media', 'Or choose from media library') }}
            </div>
            <div
              v-if="form.image && !imageFiles.length"
              class="push-image-preview mb-2"
              :style="{ backgroundImage: 'url(' + form.image + ')' }"
            >
              <b-button
                class="remove-button btn-icon"
                variant="danger"
                size="sm"
                @click="clearImage"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
            <my-menu-media-input
              v-show="!form.image || imageFiles.length"
              v-model="imageFiles"
              :single="true"
              disable-video
              @uploaded="onMediaUploaded"
              @input="onMediaInput"
              @select-file="onSelectMediaFile"
            />
          </b-form-group>

          <b-alert
            v-if="result"
            :variant="result.error === 0 ? 'success' : 'warning'"
            show
            class="mt-2"
          >
            {{ result.message }}
            <template v-if="result.tokens_sent !== undefined">
              <br>
              <small>
                {{ $t('push-notifications.users-matched', 'Users matched') }}: {{ result.users_matched }} ·
                {{ $t('push-notifications.tokens-sent', 'Tokens sent') }}: {{ result.tokens_sent }} ·
                {{ $t('push-notifications.success-count', 'Success') }}: {{ result.success_count }} ·
                {{ $t('push-notifications.failure-count', 'Failure') }}: {{ result.failure_count }}
              </small>
            </template>
          </b-alert>

          <b-button
            type="submit"
            variant="primary"
            :disabled="sending"
            class="mt-2"
          >
            <b-spinner
              v-if="sending"
              small
              class="mr-50"
            />
            {{ $t('push-notifications.send', 'Send push notification') }}
          </b-button>
        </b-form>
      </div>
    </empty-card>
  </div>
</template>

<script>
import {
  BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BAlert, BSpinner, BLink,
} from 'bootstrap-vue'
import EmptyCard from '@/@bya3/components/cards/EmptyCard'
import OkDateTimePicker from '@/@bya3/components/inputs/OkDateTimePicker'
import MyMenuMediaInput from '@/views/media/MyMenuMediaInput'
import AdminPushNotificationsApi from '@/api/admin/pushNotifications'

const defaultDateFrom = () => {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 2)
  return d.toISOString().slice(0, 10)
}
const defaultDateTo = () => new Date().toISOString().slice(0, 10)

export default {
  name: 'PushNotifications',
  components: {
    EmptyCard,
    OkDateTimePicker,
    MyMenuMediaInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BAlert,
    BSpinner,
    BLink,
  },
  data() {
    return {
      form: {
        has_active_pages: false,
        has_expired_pages: false,
        has_trial_pages: false,
        date_from: defaultDateFrom(),
        date_to: defaultDateTo(),
        subject: '',
        body: '',
        image: '',
      },
      sending: false,
      result: null,
      imageFiles: [],
    }
  },
  methods: {
    getMediaUrl(file) {
      return file?.med?.url ?? file?.url ?? file?.original?.url ?? ''
    },
    onSelectMediaFile(files) {
      const url = this.getMediaUrl(files?.[0])
      if (url) {
        this.form.image = url
        this.imageFiles = files || []
      }
    },
    onMediaUploaded(file) {
      const url = this.getMediaUrl(file)
      if (url) this.form.image = url
    },
    onMediaInput(files) {
      this.imageFiles = files || []
      if (this.imageFiles.length === 0) this.form.image = ''
    },
    clearImage() {
      this.form.image = ''
      this.imageFiles = []
    },
    async onSubmit() {
      this.sending = true
      this.result = null
      try {
        const payload = {
          subject: this.form.subject,
          body: this.form.body,
          date_from: this.form.date_from || undefined,
          date_to: this.form.date_to || undefined,
        }
        if (this.form.has_active_pages) payload.has_active_pages = true
        if (this.form.has_expired_pages) payload.has_expired_pages = true
        if (this.form.has_trial_pages) payload.has_trial_pages = true
        if (this.form.image?.trim()) payload.image = this.form.image.trim()

        const data = await AdminPushNotificationsApi.send(payload)
        this.result = data
        if (data.error === 0) {
          this.$notifySuccess(data.message)
        } else {
          this.$notifyError(data.message)
        }
      } catch (e) {
        this.$notifyError(e?.response?.data?.message || e?.message || 'Request failed')
        this.result = { error: 1, message: e?.response?.data?.message || e?.message || 'Request failed' }
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.push-notifications-form {
  max-width: 600px;
}

.push-image-preview {
  width: 100%;
  max-width: 200px;
  height: 150px;
  background-size: cover;
  background-position: center;
  border-radius: 5px;
  position: relative;
  background-color: #f0eff5;

  .remove-button {
    position: absolute;
    top: 4px;
    right: 4px;
  }
}
</style>
